/**
 * Location beacons (docs/plans/2026-07-02-location-beacons-design.md): a tall
 * semi-transparent column of light plus a billboarded text label at each named
 * place, so viewers can tell where they are while navigating — especially the
 * route fly-through, since individual houses aren't rendered.
 *
 * Purely orientation furniture. Beacon positions are approximate (OS map /
 * OpenStreetMap village centres), have NO bearing on any LOS result (hard
 * constraint #1: LOS truth is raypath's analytical raster) and no elevation is
 * ever invented — every beacon's base comes from the same collider
 * `sampleGround` that backs walk-mode ground lock (hard constraint #6).
 *
 * The pure helpers (locationToLocal, beaconLabelText) are three.js-free and
 * unit-tested in Node. BeaconsLayer wraps them in a three.js Group exposing
 * update(sampleGround), so beacons drape as terrain tiles stream in — mirrors
 * BuildingsLayer (viewer/src/scene/buildings.ts) and RouteRibbon.
 */
import {
  CanvasTexture,
  ClampToEdgeWrapping,
  CylinderGeometry,
  FrontSide,
  Group,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Sprite,
  SpriteMaterial,
  type Texture,
} from 'three';
import { bngToLocal, type LocalXZ } from '../core/coordinates';
import type { Location } from '../core/manifest';
import type { GroundSampler } from '../camera/groundLock';

/** Height of the light column, metres. Tall enough to read against the sky
 * from across the valley, comfortably above the turbines' 230 m tips only near
 * the array, but these are orientation markers, not scale references. */
export const BEAM_HEIGHT_M = 260;
/** Radius of the light column, metres. Wide enough to read as a shaft of light
 * from across the valley (a few km), narrow enough not to blanket the terrain. */
export const BEAM_RADIUS_M = 12;
/** Height up the beam at which the label floats, metres (above building/tree clutter). */
export const LABEL_HEIGHT_M = 30;

// Bright near-white with a cool tint: reads as a shaft of light and, unlike a
// saturated cyan, keeps contrast against BOTH the pale sky and the green/tan
// terrain rather than blending into the sky at distance.
const BEAM_COLOR = 0xeaf6ff;

/** Local viewer-space x/z for a location. Always routes through bngToLocal so a
 * handedness fix elsewhere keeps beacons correct (never hardcode the sign). */
export function locationToLocal(location: Pick<Location, 'easting' | 'northing'>): LocalXZ {
  return bngToLocal(location.easting, location.northing);
}

/** Menu/label text for a beacon: "Name — context", or just "Name" when there
 * is no context. */
export function beaconLabelText(location: Pick<Location, 'name' | 'context'>): string {
  const context = location.context?.trim();
  return context ? `${location.name} — ${context}` : location.name;
}

/** Vertical alpha gradient for the beam: opaque (white) at the base, fading to
 * fully transparent at the top. Multiplied against the beam's cyan colour under
 * normal blending, so the column reads as a solid shaft of light near the
 * ground that dissolves into the sky. Falls back to null where there is no 2D
 * canvas context (Node test env), in which case the beam uses a flat opacity. */
function makeBeamGradientTexture(): Texture | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const grad = ctx.createLinearGradient(0, canvas.height, 0, 0); // bottom -> top
  // Stay near-opaque for most of the height so the part that pokes ABOVE a
  // distant ridge (i.e. the mid-upper column, once the base is occluded) is
  // still clearly visible; taper only the top ~20% into the sky.
  grad.addColorStop(0, 'rgba(255,255,255,0.95)');
  grad.addColorStop(0.8, 'rgba(255,255,255,0.8)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const tex = new CanvasTexture(canvas);
  tex.wrapS = ClampToEdgeWrapping;
  tex.wrapT = ClampToEdgeWrapping;
  tex.minFilter = LinearFilter;
  return tex;
}

/** A billboarded text-label sprite for a beacon. Returns null where there is no
 * 2D canvas context (Node test env): callers then omit the label but keep the
 * beam. */
function makeLabelSprite(text: string): Sprite | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const fontPx = 48;
  const pad = 24;
  ctx.font = `600 ${fontPx}px system-ui, sans-serif`;
  const measured = ctx.measureText(text).width;
  const textW = measured > 0 ? measured : text.length * fontPx * 0.55; // happy-dom returns 0
  canvas.width = Math.ceil(textW + pad * 2);
  canvas.height = fontPx + pad * 2;

  // Re-set after the canvas resize cleared the context state.
  ctx.font = `600 ${fontPx}px system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // soft dark plate for legibility against sky or terrain
  ctx.fillStyle = 'rgba(12, 18, 24, 0.62)';
  roundRect(ctx, 0, 0, canvas.width, canvas.height, 16);
  ctx.fillStyle = '#eaf6ff';
  ctx.fillText(text, canvas.width / 2, canvas.height / 2);

  const tex = new CanvasTexture(canvas);
  tex.minFilter = LinearFilter;
  const material = new SpriteMaterial({ map: tex, transparent: true, depthWrite: false, fog: false });
  material.toneMapped = false;
  const sprite = new Sprite(material);
  // World size: keep a fixed world height; width follows the canvas aspect.
  const worldH = 14;
  const aspect = canvas.width / canvas.height;
  sprite.scale.set(worldH * aspect, worldH, 1);
  return sprite;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
  ctx.fill();
}

interface Beacon {
  /** The per-location group, positioned on the ground at update(). */
  node: Object3D;
  x: number;
  z: number;
}

export class BeaconsLayer {
  readonly group = new Group();
  private readonly beacons: Beacon[] = [];
  private readonly beamGeometry: CylinderGeometry;
  private readonly beamMaterial: MeshBasicMaterial;

  constructor(locations: Location[]) {
    // One shared geometry + material for every beam (per-beam cost is just a
    // positioned Mesh), mirroring buildings.ts's shared-geometry approach.
    this.beamGeometry = new CylinderGeometry(BEAM_RADIUS_M, BEAM_RADIUS_M, BEAM_HEIGHT_M, 12, 1, true);
    const gradient = makeBeamGradientTexture();
    this.beamMaterial = new MeshBasicMaterial({
      color: BEAM_COLOR,
      transparent: true,
      opacity: gradient ? 1 : 0.55,
      depthWrite: false,
      // FrontSide (back-face culled): from outside you see the column's near
      // wall; when the camera is at/inside a beacon (e.g. an observer standing
      // on that very spot) the back-facing walls cull away, so the column never
      // fills the screen with a wash instead of hiding.
      side: FrontSide,
      // A beacon is meant to be seen from across the valley and against the
      // sky, so it must NOT be dimmed by the cosmetic distance fog or by tone
      // mapping — those would wash the column out exactly where it's needed.
      fog: false,
    });
    if (gradient) this.beamMaterial.map = gradient;
    this.beamMaterial.toneMapped = false;

    for (const loc of locations) {
      const { x, z } = locationToLocal(loc);
      const node = new Group();
      node.position.set(x, 0, z);
      node.visible = false; // hidden until grounded by update()

      const beam = new Mesh(this.beamGeometry, this.beamMaterial);
      beam.position.y = BEAM_HEIGHT_M / 2; // base sits at the node origin (the ground)
      node.add(beam);

      const label = makeLabelSprite(beaconLabelText(loc));
      if (label) {
        label.position.y = LABEL_HEIGHT_M;
        node.add(label);
      }

      this.group.add(node);
      this.beacons.push({ node, x, z });
    }
  }

  /** Anchors each beacon on the terrain surface at the sampled ground height.
   * A beacon is grounded (and shown) the first time a loaded tile yields a real
   * DTM height under it — never a fabricated one. Once grounded it STAYS at
   * that height and visible, even if its tile later unloads as the camera moves
   * off across the valley: an orientation beacon must not blink out at a
   * distance, which is exactly where it's most useful. So a null sample is a
   * "not yet known" (leave a still-ungrounded beacon hidden), never a reason to
   * hide or move one already placed. */
  update(sampleGround: GroundSampler): void {
    for (const b of this.beacons) {
      const ground = sampleGround(b.x, b.z);
      if (ground === null) continue;
      b.node.position.y = ground;
      b.node.visible = true;
    }
  }
}
